import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { API, useAuthedApi } from "./api";

const RtCtx = createContext({ counts: { notifications: 0, chats: 0 }, refresh: () => {}, subscribe: () => () => {}, live: false });

export function RealtimeProvider({ children }) {
  const { isSignedIn, getToken } = useAuth();
  const api = useAuthedApi();
  const [counts, setCounts] = useState({ notifications: 0, chats: 0 });
  const [live, setLive] = useState(false);
  const subs = useRef(new Set());

  const refresh = useCallback(() => {
    if (isSignedIn) api("get", "/me/counts").then(setCounts).catch(() => {});
  }, [isSignedIn, api]);

  useEffect(() => {
    if (!isSignedIn) { setCounts({ notifications: 0, chats: 0 }); return; }
    refresh();
    let ws, stop = false, retry;
    const connect = async () => {
      const token = await getToken();
      if (stop || !token) return;
      ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws?token=${encodeURIComponent(token)}`);
      ws.onopen = () => setLive(true);
      ws.onmessage = (e) => {
        const ev = JSON.parse(e.data);
        subs.current.forEach((fn) => fn(ev));
        if (ev.type === "notification") {
          refresh();
          if (!window.location.pathname.startsWith(ev.notification.link || "~")) toast(ev.notification.title, { description: ev.notification.body });
        }
        if (ev.type === "message") refresh();
      };
      ws.onclose = () => { setLive(false); if (!stop) retry = setTimeout(connect, 4000); };
    };
    connect();
    const poll = setInterval(refresh, 30000);
    const ping = setInterval(() => ws?.readyState === 1 && ws.send("ping"), 25000);
    return () => { stop = true; clearTimeout(retry); clearInterval(poll); clearInterval(ping); ws?.close(); };
  }, [isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = useCallback((fn) => { subs.current.add(fn); return () => subs.current.delete(fn); }, []);
  return <RtCtx.Provider value={{ counts, refresh, subscribe, live }}>{children}</RtCtx.Provider>;
}

export const useRealtime = () => useContext(RtCtx);
